// Small refined value types shared across the entities.
//
// These exist so that "capacity" cannot be zero, "revision" cannot be a person
// count, and a half-open window cannot end before it starts — without every
// consumer re-checking. Constructors validate once at the edge; the type then
// carries the guarantee.

/**
 * A monotonically increasing version counter on an entity (EVT-007, NED-008).
 *
 * Branded so it cannot be confused with a capacity, an index or a count — all of
 * which are also small non-negative integers, and all of which would be silently
 * interchangeable as bare `number`s.
 */
export type Revision = number & { readonly __brand: unique symbol };

/**
 * How many people a need requires, or how many places one commitment fills
 * (NED-004, COV-006).
 *
 * Always at least 1. A need that requires nobody is not a need, and a commitment
 * that covers nobody is noise in the coverage count.
 */
export type Capacity = number & { readonly __brand: unique symbol };

/** Narrow a number to a {@link Revision}. Throws on a non-integer or negative input. */
export function revision(value: number): Revision {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`Revision must be a non-negative integer, got ${String(value)}`);
  }
  return value as Revision;
}

/** Narrow a number to a {@link Capacity}. Throws on anything below 1 (NED-004). */
export function capacity(value: number): Capacity {
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError(`Capacity must be an integer of at least 1, got ${String(value)}`);
  }
  return value as Capacity;
}
